import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { useThreeDView } from '@/components/Recipe/FlowContext';
import type { ThreeDProps } from '@/components/Common/ImageList/types';
import type { AiModelData, UpdateNodeData } from '@/types/nodes/model';
import type { NodeId } from 'web';

export interface CameraPosition {
  x: number;
  y: number;
  z: number;
}

export const use3DModelParameters = (
  id: NodeId,
  data: AiModelData,
  updateNodeData: ((id: string, data: Partial<AiModelData>) => void) | UpdateNodeData,
): ThreeDProps => {
  const [cameraPosition, setCameraPosition] = useState<CameraPosition>(data.cameraPosition || { x: -3, y: 4, z: 10 });

  const debouncedUpdateNodeDataRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    if (debouncedUpdateNodeDataRef.current) {
      debouncedUpdateNodeDataRef.current.cancel();
    }

    // Create new debounced function with current values
    debouncedUpdateNodeDataRef.current = debounce((position: CameraPosition) => {
      updateNodeData(id, {
        cameraPosition: position,
      });
    }, 1000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debouncedUpdateNodeDataRef.current) {
        debouncedUpdateNodeDataRef.current.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateCameraPosition = useCallback((position: CameraPosition) => {
    setCameraPosition(position);
    if (debouncedUpdateNodeDataRef.current) {
      debouncedUpdateNodeDataRef.current(position);
    }
  }, []);

  const is3DLocked = useMemo(() => {
    return data.cameraLocked ?? !!data.is3DLocked;
  }, [data.cameraLocked, data.is3DLocked]);

  const threeDView = useThreeDView(id);
  const updateIs3DLocked = useCallback(
    (locked: boolean) => {
      threeDView.setCameraLocked(locked);
    },
    [threeDView],
  );

  const threeDProps = useMemo<ThreeDProps>(
    () => ({
      cameraPosition,
      setCameraPosition: updateCameraPosition,
      is3DLocked,
      setIs3DLocked: updateIs3DLocked,
    }),
    [cameraPosition, updateCameraPosition, is3DLocked, updateIs3DLocked],
  );

  return threeDProps;
};
